<script setup>
import {computed, nextTick, onBeforeUnmount, onMounted, ref, watch} from 'vue'
import {useRoute, useRouter} from 'vue-router/composables'
import helpers from '@/lib/helpers'
import store from '@/store'
import ChatMessage from './chat-message.vue'

// embedded: the chat inside the toolbar modal - its own conversation, no sidebar, no URL changes,
// and `context` is the record or page it was opened from.
const props = defineProps({
  embedded: {type: Boolean, default: false},
  context: {type: Object, default: null},
  conversation: {type: Number, default: null},
})

const route = useRoute()
const router = useRouter()

const conversations = ref([])

// Someone else's chats (chat.view_others): whose list the sidebar shows (?user=), and an
// open chat of theirs is read only - no message box, its questions closed.
const canViewOthers = computed(() => !props.embedded && helpers.can('chat.view_others'))
const people = ref([])
const viewUser = computed(() => (!props.embedded && Number(route.query.user)) || null)
const readonly = ref(false)
const owner = ref(null)
const answering = ref(false)

async function loadPeople() {
  if (!canViewOthers.value) return
  try {
    people.value = (await helpers.asyncAction('/api/chat/users', 'GET', {quiet: true, noload: true})).users || []
  } catch {
    // the picker is a nicety: their own chats still work
  }
}

function viewPerson(id) {
  // a person's newest chat opens with their list; back to your own: a fresh chat of yours
  router.push({path: '/chat', query: id ? {user: String(id)} : {}}).catch(() => {})
}

// The suggestion bubbles and the "Things I can help with" list, all from the server and
// already limited to what this person may use.
const capabilities = ref({title: '', starters: {general: [], record: []}, groups: []})
const helpOpen = ref(false)
const entries = ref([])
const draft = ref('')
const busy = ref(false)
const loadError = ref('')
// a saved chat is on its way: show nothing rather than flash the new-chat bubbles first
const loading = ref(true)

const thread = ref(null)
const composer = ref(null)

/* ---------- files put into the chat ---------- */

// uploaded to this chat and waiting to go with the next message
const pending = ref([])
const filePicker = ref(null)
const canUpload = computed(() => helpers.can('chat.upload'))
const FILE_ACCEPT = 'image/*,.pdf,.csv,.tsv,.txt,.xlsx'

function pickFiles() {
  if (filePicker.value) filePicker.value.click()
}

async function uploadFiles(list) {
  const files = Array.from(list || [])
  if (!files.length || !conversationId.value || !canUpload.value) return
  const room = 5 - pending.value.length
  if (room <= 0) {
    helpers.toast('Up to 5 files with one message', true)
    return
  }
  const form = new FormData()
  files.slice(0, room).forEach((f, i) => form.append('file' + i, f, f.name))
  try {
    const r = await helpers.asyncAction(`/api/chat/conversation/${conversationId.value}/file`, 'POST', {data: form})
    pending.value = [...pending.value, ...(r.files || [])]
  } finally {
    if (filePicker.value) filePicker.value.value = ''
  }
  nextTick(() => composer.value && composer.value.focus())
}

function onPickFiles(e) {
  uploadFiles(e.target.files)
}

// a screenshot pasted into the box comes along as a file
function onComposerPaste(e) {
  const files = Array.from((e.clipboardData && e.clipboardData.files) || [])
  if (files.length && canUpload.value) {
    e.preventDefault()
    uploadFiles(files)
  }
}

function onComposerDrop(e) {
  const files = (e.dataTransfer && e.dataTransfer.files) || []
  if (files.length && canUpload.value) uploadFiles(files)
}

function fileIcon(f) {
  return {image: 'file-image', pdf: 'file-pdf', sheet: 'file-excel', email: 'envelope'}[f.kind] || 'file'
}

function unpend(f) {
  pending.value = pending.value.filter(p => p.id !== f.id)
}

/* ---------- the + menu ---------- */

const plusOpen = ref(false)

const plusOptions = computed(() => [
  ...(canUpload.value ? [{key: 'file', icon: 'paperclip', title: 'Add photos & files', desc: 'Upload from your computer - photos, PDF, CSV, Excel'}] : []),
  {key: 'fav', icon: 'star', iconClass: 'text-warning', title: 'Favorite prompts', desc: 'Type in a question you saved'},
  ...(canUpload.value ? [{key: 'inbox', icon: 'envelope', iconClass: 'text-primary', title: 'Email inbox',
    desc: inbox.value.address ? `Mail you sent to ${inbox.value.address}` : 'Mail you sent to the assistant', count: inbox.value.emails.length}] : []),
])

function togglePlus() {
  plusOpen.value = !plusOpen.value
  if (plusOpen.value) {
    favoritesOpen.value = inboxOpen.value = false
    loadInbox()
  }
}

function choosePlus(o) {
  plusOpen.value = false
  if (o.key === 'fav') toggleFavorites()
  else if (o.key === 'file') pickFiles()
  else if (o.key === 'inbox') toggleInbox()
}

// a click anywhere else, or Escape, closes it
function closePlus(e) {
  if (plusOpen.value && (e.type !== 'keydown' || e.key === 'Escape')) plusOpen.value = false
}

onMounted(() => {
  document.addEventListener('click', closePlus)
  document.addEventListener('keydown', closePlus)
})
onBeforeUnmount(() => {
  document.removeEventListener('click', closePlus)
  document.removeEventListener('keydown', closePlus)
})

/* ---------- favorite prompts (users.config, same list in both apps) ---------- */

const favorites = ref([])
const favoritesOpen = ref(false)

async function loadFavorites() {
  try {
    favorites.value = (await helpers.asyncAction('/api/chat/favorites', 'GET', {quiet: true, noload: true})).favorites || []
  } catch {
    // optional
  }
}

async function addFavorite(text) {
  const r = await helpers.asyncAction('/api/chat/favorites', 'POST', {data: {text}, noload: true})
  favorites.value = r.favorites || []
  helpers.toast('Saved to your favorite prompts')
}

async function removeFavorite(text) {
  const r = await helpers.asyncAction('/api/chat/favorites', 'PUT', {data: {favorites: favorites.value.filter(f => f !== text)}, noload: true})
  favorites.value = r.favorites || []
}

// typed in for them, not sent: they can change it first
function useFavorite(text) {
  draft.value = text
  favoritesOpen.value = false
  nextTick(() => composer.value && composer.value.focus())
}

async function toggleFavorites() {
  inboxOpen.value = false
  favoritesOpen.value = !favoritesOpen.value
  if (favoritesOpen.value) await loadFavorites()
}

/* ---------- the assistant's email inbox (mail sent to the assistant address) ---------- */

const inbox = ref({address: null, hours: 12, ready: false, emails: []})
const inboxOpen = ref(false)

async function loadInbox() {
  if (!canUpload.value) return
  try {
    inbox.value = await helpers.asyncAction('/api/chat/inbox', 'GET', {quiet: true, noload: true})
  } catch {
    // the inbox is optional; uploads still work
  }
}

async function toggleInbox() {
  favoritesOpen.value = false
  inboxOpen.value = !inboxOpen.value
  if (inboxOpen.value) await loadInbox()
}

// an email (its text and its attachments) goes with the next message
async function useEmail(e) {
  if (!conversationId.value) return
  const r = await helpers.asyncAction(`/api/chat/conversation/${conversationId.value}/inbox/${e.id}`, 'POST')
  const have = new Set(pending.value.map(p => p.id))
  pending.value = [...pending.value, ...(r.files || []).filter(f => !have.has(f.id))]
  inboxOpen.value = false
  loadInbox()
  nextTick(() => composer.value && composer.value.focus())
}

async function discardEmail(e) {
  await helpers.asyncAction(`/api/chat/inbox/${e.id}`, 'DELETE', {quiet: true})
  inbox.value.emails = inbox.value.emails.filter(x => x.id !== e.id)
}

function waitingFor(e) {
  const mins = Math.max(0, Math.round((new Date(e.expires.replace(' ', 'T')) - new Date()) / 60000))
  return mins >= 60 ? `${Math.floor(mins / 60)}h ${mins % 60}m left` : `${mins}m left`
}
const pageContext = ref(props.embedded ? props.context : null)
const localConversation = ref(null)

const welcomeTitle = computed(() =>
    pageContext.value && pageContext.value.gli ? `How can I help with ${pageContext.value.gli}?` : 'What can I help with?')
const starters = computed(() => capabilities.value.starters[pageContext.value && pageContext.value.gli ? 'record' : 'general'])

// Tool calls are hidden unless switched on (click "Recent" in the sidebar 5 times, or open /chat?tools=1).
const SHOW_TOOLS_KEY = 'oms.chat.showTools'
function readShowTools() {
  try {
    return localStorage.getItem(SHOW_TOOLS_KEY) === '1'
  } catch {
    return false
  }
}

const showTools = ref(readShowTools() || route.query.tools === '1')
let titleClicks = []

function setShowTools(on) {
  showTools.value = on
  try {
    localStorage.setItem(SHOW_TOOLS_KEY, on ? '1' : '0')
  } catch {
    // storage unavailable: applies to this page view only
  }
}

function titleClick() {
  const now = Date.now()
  titleClicks = titleClicks.filter(t => now - t < 2000).concat(now)
  if (titleClicks.length >= 5) {
    titleClicks = []
    setShowTools(!showTools.value)
  }
}

const WORKING_LABELS = {
  oms_schema: 'Checking what data is available',
  oms_query: 'Looking through the records',
  oms_find: 'Searching for the record',
  oms_availability: 'Checking stock',
  oms_report: 'Running the report',
  oms_note_add: 'Preparing the note',
  oms_data: 'Analysing the data',
  oms_ship_quote: 'Getting shipping rates',
  oms_knowledge: 'Reading up on how this works',
  oms_read_file: 'Reading the file',
  oms_ocr_file: 'Reading the file',
  fetch_ebay_item: 'Checking eBay',
  ebay_active_search: 'Searching eBay listings',
  ebay_completed_search: 'Searching eBay sales',
  google_search: 'Searching the web',
  fetch_url: 'Reading a web page',
  oms_notify_catalog: 'Checking what can notify you',
  oms_notify_create: 'Setting up the notification',
  oms_notify_list: 'Looking at your notifications',
  oms_notify_remove: 'Removing the notification',
}

const working = ref(false)
const workingLabel = ref('Working')

async function cancelNotice(entry) {
  entry.state = 'cancelling'
  try {
    await helpers.asyncAction(`/api/user/self/notification/rule/${entry.rule.rule_id}`, 'DELETE', {quiet: true, noload: true})
    entry.state = 'cancelled'
  } catch (e) {
    // a one-off rule is gone once it has fired
    entry.state = e?.response?.status === 404 ? 'gone' : 'active'
    if (entry.state === 'active') helpers.toast('Could not cancel that notification', true)
  }
}

const visibleEntries = computed(() =>
    entries.value.map((entry, i) => ({entry, i})).filter(x => showTools.value || x.entry.kind !== 'tool'))

const conversationId = computed(() => (props.embedded ? localConversation.value : route.params.conversation) || null)

function scrollDown() {
  nextTick(() => {
    if (thread.value) thread.value.scrollTop = thread.value.scrollHeight
  })
}

async function loadCapabilities() {
  try {
    const r = await helpers.asyncAction('/api/chat/capabilities', 'GET', {quiet: true, noload: true})
    capabilities.value = {title: r.title, starters: r.starters, groups: r.groups}
  } catch {
    // the bubbles are a nicety; the chat works without them
  }
}

async function loadConversations() {
  if (props.embedded) return
  const r = await helpers.asyncAction('/api/chat/conversation', 'GET', {quiet: true, noload: true, data: viewUser.value ? {user: viewUser.value} : undefined})
  conversations.value = r.conversations || []
}

function openConversation(id) {
  router.push({path: `/chat/${id}`, query: viewUser.value ? {user: String(viewUser.value)} : {}})
}

// the saved turns as chat entries; a question the assistant asked comes back as its buttons,
// still answerable only when it is the last thing in the chat
function savedEntries(turns, canAnswer) {
  const out = []
  turns.forEach((t, i) => {
    if (t.role === 'file') {
      out.push({kind: 'file', type: t.type, name: t.name, url: t.url, sheets: t.sheets || [], expires: t.expires})
      return
    }
    if (t.role === 'photos') {
      if ((t.photos || []).length) out.push({kind: 'photos', photos: t.photos})
      return
    }
    if (!t.ask) {
      out.push({kind: t.role === 'user' ? 'user' : 'bot', text: t.content, files: t.files || []})
      return
    }
    if (t.shown) out.push({kind: 'bot', text: t.shown, files: []})
    const next = turns.slice(i + 1).find(x => x.role === 'user')
    const options = t.ask.options || []
    out.push({
      kind: 'choice', question: t.ask.question, options, multiple: !!t.ask.multiple,
      state: !next && canAnswer && i === turns.length - 1 ? 'open' : 'closed',
      picked: next ? options.filter(o => next.content.split(/\s*,\s*|\n/).includes(o)) : [],
    })
  })
  return out
}

async function loadConversation(id) {
  stopWatching()
  entries.value = []
  loadError.value = ''
  if (!id) return
  loading.value = true
  try {
    const r = await helpers.asyncAction(`/api/chat/conversation/${id}`, 'GET', {quiet: true, noload: true})
    readonly.value = !!r.readonly
    owner.value = r.owner || null
    answering.value = !!r.answering
    // files made in this chat on any device (the phone app too) come back as their own entries
    entries.value = savedEntries(r.turns || [], !r.running && !r.readonly)
    scrollDown()
    // an answer still being written (maybe started on another page): show it arriving
    if (r.running) {
      entries.value.push({kind: 'user', text: r.running.message})
      follow(r.running.turn, '0')
    }
    if (!props.embedded) loadConversations()
    // opening it read it: the navbar icon drops it from its list
    store.dispatch('chat_load')
  } catch (e) {
    loadError.value = e.message || 'Could not load conversation'
  } finally {
    loading.value = false
  }
}

// a chat opened from a record belongs to it, so the record's icon finds it again
const recordGli = computed(() => (props.embedded && pageContext.value && pageContext.value.gli) || null)

function createConversation() {
  return helpers.asyncAction('/api/chat/conversation', 'POST', {noload: true, data: {record_gli: recordGli.value}})
}

async function newChat() {
  stopWatching()
  // reading someone else's chats: a new chat is your own, back in your list
  if (viewUser.value) {
    router.push({path: '/chat'}).catch(() => {})
    return
  }
  const r = await createConversation()
  if (props.embedded) {
    entries.value = []
    localConversation.value = r.id
    focusComposer()
    return
  }
  await loadConversations()
  router.push(`/chat/${r.id}`)
}

function focusComposer() {
  nextTick(() => composer.value && composer.value.focus())
}

// Opening the assistant starts a fresh chat; only a link to a specific chat reopens one.
async function startFresh() {
  const r = await createConversation()
  if (props.embedded) {
    localConversation.value = r.id
    focusComposer()
    return
  }
  await loadConversations()
  router.replace(`/chat/${r.id}`).catch(() => {})   // remounts; a pending ask stays set
}

// Enter sends, Shift+Enter adds a line (the field grows with its text).
function onComposerKey(e) {
  if (e.key === 'Enter' && !e.shiftKey && !e.isComposing) {
    e.preventDefault()
    send()
  }
}

function useStarter(text) {
  draft.value = text
  send()
}

// How many times in a row the person has let the assistant carry on past its tool-call limit.
const MAX_CONTINUES = 3
const CONTINUE_TEXT = 'Please carry on from where you stopped. Do not repeat searches you already made.'
const continues = ref(0)

function continueRun() {
  if (busy.value || continues.value >= MAX_CONTINUES) return
  continues.value++
  send(CONTINUE_TEXT, true)
}

function answerChoice(entry, picked) {
  if (busy.value || entry.state !== 'open' || !picked.length) return
  entry.picked = picked
  entry.state = 'answered'
  send(picked.join(', '))
}

async function send(forced, continuing) {
  const text = (typeof forced === 'string' ? forced : draft.value).trim()
  const files = typeof forced === 'string' ? [] : pending.value
  if ((!text && !files.length) || busy.value || !conversationId.value) return
  if (files.length) pending.value = []
  if (!continuing) {
    continues.value = 0
    draft.value = ''
  }
  entries.value = entries.value.filter(e => e.kind !== 'limit')
  entries.value.forEach(e => {
    if (e.kind === 'choice' && e.state === 'open') e.state = 'closed'
  })
  entries.value.push({kind: 'user', text, files})
  scrollDown()

  const run = startRun()
  try {
    // the answer is written by a background job; this request relays it as it comes
    const res = await fetch(`/api/chat/conversation/${conversationId.value}/message`, {
      method: 'POST',
      headers: {'Content-Type': 'application/json', Accept: 'text/event-stream'},
      credentials: 'include',
      body: JSON.stringify({message: text, files: files.map(f => f.id), context: pageContext.value}),
      signal: run.abort.signal,
    })
    await readEvents(res, run)
    // the connection dropped before the end: the job carries on, so pick it back up
    if (!run.done && run.turn && !run.abort.signal.aborted) await follow(run.turn, run.after, run)
  } catch (e) {
    if (!run.abort.signal.aborted) entries.value.push({kind: 'error', text: e.message || 'Request failed'})
  } finally {
    endRun(run)
  }
}

/* ---------- following an answer ---------- */

// The answer finished on screen, so it has been seen. A chat closed before the end
// never gets here and stays unread for the assistant icon's menu.
async function markRead() {
  const id = conversationId.value
  if (!id) return
  try {
    await helpers.asyncAction(`/api/chat/conversation/${id}/read`, 'POST', {quiet: true, noload: true})
  } catch {
    // the menu just lists it until it is opened
  }
  store.dispatch('chat_load')
}

// The one answer this view is showing; replaced (and its connection closed) when the view moves on.
let current = null

function startRun() {
  stopWatching()
  current = {abort: new AbortController(), bot: null, pendingTool: null, turn: null, after: '0', done: false}
  busy.value = true
  working.value = true
  workingLabel.value = 'Working'
  return current
}

function endRun(run) {
  if (current !== run) return
  current = null
  store.dispatch('chat_load')
  working.value = false
  busy.value = false
  if (!props.embedded) loadConversations()
  scrollDown()
}

// Leaving the page or the chat only stops watching; the answer keeps being written.
function stopWatching() {
  if (current) {
    current.abort.abort()
    current = null
  }
  working.value = false
  busy.value = false
}

// Attach to a turn's events from `after` ("0" = replay from the start), reconnecting if the line drops.
async function follow(turn, after, existing) {
  const run = existing || startRun()
  run.turn = turn
  run.after = after || run.after
  try {
    for (let tries = 0; tries < 5 && !run.done && !run.abort.signal.aborted; tries++) {
      const res = await fetch(`/api/chat/turn/${turn}/events?after=${encodeURIComponent(run.after)}`, {
        headers: {Accept: 'text/event-stream'},
        credentials: 'include',
        signal: run.abort.signal,
      })
      await readEvents(res, run)
    }
  } catch (e) {
    if (!run.abort.signal.aborted) entries.value.push({kind: 'error', text: e.message || 'Lost the connection'})
  } finally {
    if (!existing) endRun(run)
  }
}

async function readEvents(res, run) {
  if (res.status === 401) {
    window.location.reload()
    return
  }
  if (!res.ok || !res.body) {
    const data = await res.json().catch(() => ({}))
    throw new Error(data.message || `HTTP ${res.status}`)
  }

  const reader = res.body.getReader()
  const dec = new TextDecoder()
  let buf = ''
  for (;;) {
    const chunk = await reader.read()
    if (chunk.done) break
    buf += dec.decode(chunk.value, {stream: true})
    const frames = buf.split('\n\n')
    buf = frames.pop()
    for (const frame of frames) {
      const lines = frame.split('\n')
      const idLine = lines.find(l => l.startsWith('id: '))
      const line = lines.find(l => l.startsWith('data: '))
      if (!line) continue
      let ev
      try {
        ev = JSON.parse(line.slice(6))
      } catch {
        continue
      }
      if (idLine) run.after = idLine.slice(4).trim()
      applyEvent(ev, run)
      if (run.done) return
    }
  }
}

function applyEvent(ev, run) {
  if (ev.type === 'turn') {
    run.turn = ev.turn
    store.dispatch('chat_load')
  } else if (ev.type === 'token') {
    working.value = false
    if (!run.bot) {
      run.bot = {kind: 'bot', text: ''}
      entries.value.push(run.bot)
    }
    run.bot.text += ev.text
    scrollDown()
  } else if (ev.type === 'tool_call') {
    working.value = true
    workingLabel.value = WORKING_LABELS[ev.name] || 'Working'
    run.pendingTool = {
      kind: 'tool', name: ev.name, args: ev.args, pending: true,
      ok: null, status: null, route: null, summary: '', data: null,
    }
    entries.value.push(run.pendingTool)
    scrollDown()
  } else if (ev.type === 'tool_result') {
    if (run.pendingTool) {
      Object.assign(run.pendingTool, {
        pending: false, ok: ev.ok, status: ev.status,
        route: ev.route, summary: ev.summary, data: ev.data,
      })
      run.pendingTool = null
    }
    if (ev.name === 'oms_ask' && ev.ok && ev.data && Array.isArray(ev.data.options)) {
      entries.value.push({
        kind: 'choice', question: ev.data.question, options: ev.data.options,
        multiple: !!ev.data.multiple, state: 'open', picked: [],
      })
      working.value = false
      scrollDown()
    }
    if ((ev.name === 'oms_export' || ev.name === 'oms_pdf') && ev.ok && ev.data && ev.data.download_url) {
      entries.value.push({kind: 'file', type: ev.name === 'oms_pdf' ? 'pdf' : 'xlsx', name: ev.data.file_name, url: ev.data.download_url, sheets: ev.data.sheets || [],
        expires: new Date(Date.now() + (ev.data.expires_in_minutes || 0) * 60000).toISOString()})
      scrollDown()
    }
    if (ev.name === 'oms_notify_create' && ev.ok && ev.data && ev.data.rule) {
      entries.value.push({kind: 'notice', rule: ev.data.rule, created: ev.data.created !== false, state: 'active'})
      scrollDown()
    }
  } else if (ev.type === 'photos') {
    // photos the assistant found, shown as a gallery
    if ((ev.photos || []).length) {
      entries.value.push({kind: 'photos', photos: ev.photos})
      scrollDown()
    }
  } else if (ev.type === 'message') {
    working.value = false
    if (!run.bot) {
      run.bot = {kind: 'bot', text: ''}
      entries.value.push(run.bot)
    }
    if (ev.text) run.bot.text = ev.text
    scrollDown()
  } else if (ev.type === 'limit') {
    working.value = false
    entries.value.push({kind: 'limit', calls: ev.calls, canContinue: continues.value < MAX_CONTINUES})
    scrollDown()
  } else if (ev.type === 'error') {
    entries.value.push({kind: 'error', text: ev.message})
  } else if (ev.type === 'done') {
    run.done = true
    markRead()
  }
}

defineExpose({newChat, conversationId})

onMounted(async () => {
  loadCapabilities()
  loadPeople()
  try {
    await loadConversations()
    if (props.embedded) {
      await openEmbedded()
      return
    }
    if (!conversationId.value) {
      await openDefault()
      return
    }
    await loadConversation(conversationId.value)
  } finally {
    loading.value = false
  }
})

onBeforeUnmount(stopWatching)

// The assistant icon: on a record, back to that record's chat; otherwise to an answer
// that finished while they were away; otherwise a fresh chat.
async function openEmbedded() {
  if (props.conversation) {
    localConversation.value = props.conversation
    await loadConversation(props.conversation)
    store.dispatch('badges_load')
    focusComposer()
    return
  }
  try {
    const find = recordGli.value ? {record: recordGli.value} : {unread: 1}
    const r = await helpers.asyncAction('/api/chat/conversation', 'GET', {quiet: true, noload: true, params: find})
    const found = (r.conversations || [])[0]
    if (found) {
      localConversation.value = found.id
      await loadConversation(found.id)
      store.dispatch('badges_load')
      focusComposer()
      return
    }
  } catch {
    // fall through to a new chat
  }
  await startFresh()
}

watch(conversationId, (id) => {
  if (props.embedded || !id) return
  pageContext.value = null
  loadConversation(id)
})

// with no chat in the address: your own fresh chat, or the newest of the person being viewed
async function openDefault() {
  if (!viewUser.value)
    return startFresh()
  readonly.value = true
  entries.value = []
  const newest = conversations.value[0]
  if (newest)
    router.replace({path: `/chat/${newest.id}`, query: {user: String(viewUser.value)}}).catch(() => {})
}

// another person picked (or back to your own): their list, and a chat to show
watch(viewUser, async () => {
  if (props.embedded) return
  await loadConversations()
  if (!conversationId.value)
    await openDefault()
})
</script>

<template>
  <div class="d-flex flex-fill overflow-hidden chat-root" :class="{'chat-embedded': embedded}">
    <!-- sidebar -->
    <div v-if="!embedded" class="chat-sidebar d-flex flex-column">
      <div class="p-2">
        <button type="button" class="chat-new" @click="newChat">
          <font-awesome-icon icon="fa-solid fa-pen-to-square" class="mr-2"/>
          New chat
        </button>
      </div>
      <!-- whose chats: yours, or (chat.view_others) anyone's, to read -->
      <div v-if="canViewOthers && people.length" class="px-2 pb-1">
        <b-form-select size="sm" :value="viewUser" @change="viewPerson" title="Whose chats">
          <b-form-select-option :value="null">My chats</b-form-select-option>
          <b-form-select-option v-for="p in people.filter(p => !p.me)" :key="p.id" :value="Number(p.id)">
            {{ p.name || p.userid }} ({{ p.chats }})
          </b-form-select-option>
        </b-form-select>
      </div>
      <!-- 5 quick clicks on "Recent" show the tool calls (or open /chat?tools=1) -->
      <div class="chat-history-label px-3 pt-2 pb-1" @click="titleClick">Recent</div>
      <div class="flex-fill overflow-auto px-2 pb-2">
        <button
            v-for="c in conversations" :key="c.id"
            type="button"
            class="chat-history-item"
            :class="{active: String(c.id) === String(conversationId), unread: c.unread}"
            :title="(c.unread ? 'New answer - ' : '') + (c.title || 'Untitled')"
            @click="openConversation(c.id)">
          <span v-if="c.unread" class="chat-unread-dot"/>
          {{ c.title || 'Untitled' }}
        </button>
      </div>
    </div>

    <!-- main -->
    <div class="d-flex flex-column flex-fill overflow-hidden chat-main">
      <div v-if="showTools" class="px-4 py-2 d-flex align-items-center chat-header">
        <span v-if="showTools" class="badge badge-warning ml-2 cursor-pointer" title="Click to hide tool calls"
              @click="setShowTools(false)">tool calls shown</span>
      </div>

      <!-- someone else's chat: whose, and that it is read only -->
      <div v-if="readonly && !embedded" class="chat-readonly px-4 py-2 d-flex align-items-center">
        <font-awesome-icon icon="fa-solid fa-eye" class="mr-2"/>
        <span>
          <template v-if="owner">{{ owner.name || owner.userid }}'s chat</template><template v-else>Another person's chat</template>
          - read only<template v-if="answering">; an answer is still being written</template>
        </span>
        <b-button variant="link" size="sm" class="ml-auto p-0" @click="viewPerson(null)">Back to my chats</b-button>
      </div>

      <div ref="thread" class="flex-fill overflow-auto chat-thread">
        <div class="chat-column d-flex flex-column px-3 py-4" :class="{'chat-empty': conversationId && !entries.length}">
          <b-alert :show="!!loadError" variant="danger">{{ loadError }}</b-alert>

          <template v-if="loading"/>

          <div v-else-if="!conversationId" class="text-muted m-auto text-center">
            <template v-if="viewUser">No chats.</template>
            <template v-else>Start a new chat.</template>
          </div>

          <template v-else>
            <div v-if="!entries.length && readonly" class="text-muted m-auto text-center">Nothing in this chat.</div>
            <div v-else-if="!entries.length" class="m-auto text-center chat-welcome">
              <h2 class="chat-welcome-title">{{ welcomeTitle }}</h2>
              <div class="chat-starters">
                <button v-for="s in starters" :key="s" type="button" class="chat-starter" @click="useStarter(s)">
                  {{ s }}
                </button>
                <button v-if="capabilities.groups.length" type="button" class="chat-starter chat-starter-help"
                        :class="{'chat-starter-open': helpOpen}" @click="helpOpen = !helpOpen">
                  {{ capabilities.title }}
                  <font-awesome-icon :icon="helpOpen ? 'fa-solid fa-chevron-up' : 'fa-solid fa-chevron-down'" class="ml-2 small"/>
                </button>
              </div>

              <div v-if="helpOpen && capabilities.groups.length" class="chat-can">
                <div v-for="g in capabilities.groups" :key="g.title" class="chat-can-group">
                  <div class="chat-can-group-title">{{ g.title }}</div>
                  <ul>
                    <li v-for="item in g.items" :key="item.text">
                      <button v-if="item.ask" type="button" class="chat-can-link" :title="item.ask" @click="useStarter(item.ask)">{{ item.text }}</button>
                      <template v-else>{{ item.text }}</template>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            <chat-message v-for="x in visibleEntries" :key="x.i" :entry="x.entry" class="mb-4" @cancel-notice="cancelNotice(x.entry)" @continue="continueRun" @favorite="addFavorite"
                          :disabled="busy" @answer="answerChoice(x.entry, $event)"/>

            <div v-if="working" class="chat-working mb-4">
              <span class="chat-pulse"/>{{ workingLabel }}
            </div>
          </template>
        </div>
      </div>

      <form v-if="!readonly" class="chat-composer-wrap px-3 pb-3" @submit.prevent="send" @dragover.prevent @drop.prevent="onComposerDrop">
        <!-- the + menu: a full-width list above the box, icon + name + what it does -->
        <div v-if="plusOpen" class="chat-plus mb-2" @click.stop>
          <button v-for="o in plusOptions" :key="o.key" type="button" class="chat-plus-row" @click="choosePlus(o)">
            <font-awesome-icon :icon="['fas', o.icon]" class="chat-plus-icon" :class="o.iconClass"/>
            <span class="chat-plus-title">{{ o.title }}</span>
            <span class="chat-plus-desc">{{ o.desc }}</span>
            <b-badge v-if="o.count" variant="primary" pill class="ml-auto">{{ o.count }}</b-badge>
          </button>
        </div>

        <!-- favorite prompts: a tap types one in -->
        <div v-if="favoritesOpen" class="chat-inbox mb-2">
          <div class="d-flex align-items-center mb-1">
            <strong class="mr-auto"><font-awesome-icon icon="fa-solid fa-star" class="mr-1 text-warning"/>Favorite prompts</strong>
            <b-link class="small" @click="favoritesOpen = false">Close</b-link>
          </div>
          <div v-if="!favorites.length" class="small text-muted">
            No favorites yet. Double-click one of your own messages in a chat (on the phone, tap it twice) to save it here.
          </div>
          <div v-for="f in favorites" :key="f" class="chat-inbox-row d-flex align-items-center">
            <b-link class="flex-fill mr-2 text-body chat-fav" @click="useFavorite(f)">{{ f }}</b-link>
            <b-button size="sm" variant="link" class="text-muted p-0" title="Remove" @click="removeFavorite(f)">
              <font-awesome-icon icon="fa-solid fa-xmark"/>
            </b-button>
          </div>
        </div>

        <!-- emails sent to the assistant address, waiting to be used -->
        <div v-if="inboxOpen" class="chat-inbox mb-2">
          <div class="d-flex align-items-center mb-1">
            <strong class="mr-auto"><font-awesome-icon icon="fa-solid fa-envelope" class="mr-1"/>Email inbox</strong>
            <b-link class="small" @click="inboxOpen = false">Close</b-link>
          </div>
          <div class="small text-muted mb-2" v-if="inbox.address">
            <template v-if="inbox.ready">
              Send or forward email to <strong>{{ inbox.address }}</strong> from your work email. It waits here {{ inbox.hours }} hours.
            </template>
            <template v-else>
              Your work email is not set on your user yet - ask an admin, then mail <strong>{{ inbox.address }}</strong> from it.
            </template>
          </div>
          <div v-if="!inbox.emails.length" class="small text-muted">Nothing waiting. Mail can take a minute or two to arrive.</div>
          <div v-for="e in inbox.emails" :key="e.id" class="chat-inbox-row d-flex align-items-start">
            <div class="flex-fill mr-2" style="min-width: 0">
              <div class="font-weight-bold text-truncate">{{ e.subject || '(no subject)' }}</div>
              <div class="small text-muted text-truncate">{{ e.preview }}</div>
              <div class="small text-muted">
                <span v-if="e.files.length">{{ e.files.length }} attachment{{ e.files.length > 1 ? 's' : '' }} &middot; </span>
                {{ waitingFor(e) }}<span v-if="e.used"> &middot; used</span>
                <span v-if="e.skipped.length" class="text-warning"> &middot; not kept: {{ e.skipped.join(', ') }}</span>
              </div>
            </div>
            <b-button size="sm" variant="primary" class="mr-1" @click="useEmail(e)">Add</b-button>
            <b-button size="sm" variant="outline-secondary" title="Discard" @click="discardEmail(e)">
              <font-awesome-icon icon="fa-solid fa-trash"/>
            </b-button>
          </div>
        </div>

        <!-- files waiting to go with the next message -->
        <div v-if="pending.length" class="d-flex flex-wrap mb-2 chat-pending">
          <span v-for="f in pending" :key="f.id" class="chat-file-chip">
            <font-awesome-icon :icon="['fas', fileIcon(f)]" class="mr-1"/>{{ f.name }}
            <button type="button" class="chat-file-x" title="Remove" @click="unpend(f)">&times;</button>
          </span>
        </div>
        <div class="chat-composer">
          <button type="button" class="chat-attach" title="Add" :class="{'chat-attach-on': plusOpen}"
                  :disabled="!conversationId || busy" @click.stop="togglePlus">
            <font-awesome-icon icon="fa-solid fa-plus"/>
          </button>
          <input ref="filePicker" type="file" multiple :accept="FILE_ACCEPT" class="d-none" @change="onPickFiles">
          <b-form-textarea
              ref="composer"
              v-model="draft"
              :disabled="!conversationId"
              placeholder="Ask about orders, items, bids, POs…"
              rows="1"
              max-rows="8"
              no-resize
              class="chat-input"
              @keydown.native="onComposerKey"
              @paste.native="onComposerPaste"/>
          <button type="submit" class="chat-send" title="Send" :disabled="!conversationId || busy || (!draft.trim() && !pending.length)">
            <font-awesome-icon icon="fa-solid fa-arrow-up"/>
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<style scoped>
.chat-root {
  min-height: 0;
  background: #fff;
}

.chat-embedded {
  height: 100%;
}

.chat-readonly {
  background: #fff8e1;
  border-bottom: 1px solid #ffe08a;
  color: #6b5200;
  font-size: .875rem;
}

.chat-sidebar {
  width: 260px;
  min-width: 260px;
  background: #f9f9f9;
}

.chat-new,
.chat-history-item {
  display: block;
  width: 100%;
  border: 0;
  border-radius: .6rem;
  background: none;
  color: #0d0d0d;
  text-align: left;
  font-size: .9rem;
  padding: .5rem .75rem;
  cursor: pointer;
}

.chat-new:hover,
.chat-history-item:hover {
  background: #ececec;
}

.chat-history-item {
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.chat-history-item.active {
  background: #e8e8e8;
}

.chat-history-label {
  color: #8f8f8f;
  font-size: .75rem;
  font-weight: 600;
}

.chat-main {
  min-width: 0;
}

.chat-header {
  border-bottom: 1px solid #f0f0f0;
}

.chat-thread {
  min-height: 0;
}

.chat-column {
  width: 100%;
  max-width: 768px;
  min-height: 100%;
  margin: 0 auto;
}

.chat-empty {
  justify-content: center;
}

.chat-title {
  user-select: none;
}

.cursor-pointer {
  cursor: pointer;
}

.chat-welcome-title {
  font-size: 1.75rem;
  font-weight: 600;
  margin-bottom: 1.5rem;
}

.chat-starters {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: .6rem;
  text-align: left;
}

.chat-starter {
  border: 1px solid #e5e5e5;
  border-radius: 1rem;
  background: #fff;
  color: #0d0d0d;
  padding: .75rem 1rem;
  font-size: .9rem;
  text-align: left;
  cursor: pointer;
}

.chat-starter:hover {
  background: #f7f7f8;
}

.chat-starter-help {
  background: #f4f4f5;
  font-weight: 600;
}

.chat-starter-open {
  border-color: #c4c4c4;
}

.chat-can {
  margin-top: 1.25rem;
  padding: 1rem 1.25rem;
  border-radius: 1rem;
  background: #f4f4f5;
  text-align: left;
}

.chat-can-group + .chat-can-group {
  margin-top: .75rem;
}

.chat-can-group-title {
  font-size: .75rem;
  font-weight: 600;
  letter-spacing: .04em;
  text-transform: uppercase;
  color: #6b6b6b;
}

.chat-can ul {
  margin: .2rem 0 0;
  padding-left: 1.1rem;
  font-size: .9rem;
}

.chat-can-link {
  padding: 0;
  border: 0;
  background: none;
  color: inherit;
  text-align: left;
  text-decoration: underline dotted #9a9a9a;
  text-underline-offset: 3px;
  cursor: pointer;
}

.chat-can-link:hover {
  text-decoration-style: solid;
}

.chat-composer-wrap {
  width: 100%;
  max-width: 800px;
  margin: 0 auto;
}

.chat-composer {
  display: flex;
  align-items: flex-end;
  gap: .5rem;
  padding: .4rem .5rem .4rem 1rem;
  border: 1px solid #e0e0e0;
  border-radius: 1.6rem;
  background: #fff;
  box-shadow: 0 2px 10px rgba(0, 0, 0, .06);
}

.chat-composer:focus-within {
  border-color: #c4c4c4;
}

.chat-input {
  border: 0;
  box-shadow: none;
  background: transparent;
  padding: .45rem 0;
  line-height: 1.5;
}

/* bootstrap-vue gives an auto-growing textarea a permanent scrollbar; its empty
   track showed as a gray bar beside Send. Long text still scrolls. */
.chat-input {
  scrollbar-width: none;
}

.chat-input::-webkit-scrollbar {
  display: none;
}

.chat-input:focus {
  box-shadow: none;
  background: transparent;
}

.chat-attach {
  flex: none;
  width: 2.2rem;
  height: 2.2rem;
  border: 0;
  border-radius: 50%;
  background: transparent;
  color: #5d5d5d;
  cursor: pointer;
}

.chat-attach:hover:not(:disabled) {
  background: #f0f0f0;
}

.chat-inbox {
  border: 1px solid #e0e0e0;
  border-radius: 1rem;
  padding: .6rem .8rem;
  background: #fafafa;
  max-height: 320px;
  overflow-y: auto;
}

.chat-plus {
  border: 1px solid #e0e0e0;
  border-radius: 1rem;
  padding: .35rem;
  background: #fff;
  box-shadow: 0 4px 16px rgba(0, 0, 0, .08);
}

.chat-plus-row {
  display: flex;
  align-items: center;
  gap: .6rem;
  width: 100%;
  padding: .45rem .6rem;
  border: 0;
  border-radius: .6rem;
  background: transparent;
  text-align: left;
  cursor: pointer;
}

.chat-plus-row:hover,
.chat-plus-row:focus {
  background: #f2f2f3;
  outline: none;
}

.chat-plus-icon {
  width: 1rem;
  color: #5d5d5d;
}

.chat-plus-title {
  font-weight: 500;
  color: #0d0d0d;
  white-space: nowrap;
}

.chat-plus-desc {
  font-size: .85rem;
  color: #8a8a8a;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
}

.chat-attach-on {
  background: #f0f0f0 !important;
}

.chat-fav {
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}

.chat-inbox-row {
  padding: .45rem 0;
  border-top: 1px solid #ececec;
}

.chat-file-chip {
  display: inline-flex;
  align-items: center;
  max-width: 100%;
  margin: 0 .4rem .3rem 0;
  padding: .2rem .3rem .2rem .7rem;
  border: 1px solid #e0e0e0;
  border-radius: 1rem;
  background: #f7f7f8;
  font-size: .85rem;
  overflow-wrap: anywhere;
}

.chat-file-x {
  border: 0;
  background: transparent;
  margin-left: .25rem;
  font-size: 1.1rem;
  line-height: 1;
  color: #777;
  cursor: pointer;
}

.chat-send {
  flex: none;
  width: 2.2rem;
  height: 2.2rem;
  border: 0;
  border-radius: 50%;
  background: #0d0d0d;
  color: #fff;
  cursor: pointer;
}

.chat-send:disabled {
  background: #d7d7d7;
  cursor: default;
}

.chat-working {
  display: flex;
  align-items: center;
  color: #6b6b6b;
}

.chat-pulse {
  width: .7rem;
  height: .7rem;
  margin-right: .6rem;
  border-radius: 50%;
  background: #0d0d0d;
  animation: chat-pulse 1.2s ease-in-out infinite;
}

@keyframes chat-pulse {
  0%, 100% {
    transform: scale(.7);
    opacity: .4;
  }
  50% {
    transform: scale(1);
    opacity: 1;
  }
}

@media (max-width: 767px) {
  .chat-sidebar {
    display: none;
  }

  .chat-starters {
    grid-template-columns: 1fr;
  }
}

.chat-history-item.unread {
  font-weight: 600;
}

.chat-unread-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #0b57d0;
  margin-right: 6px;
  vertical-align: middle;
}
</style>
